import { lazy, Suspense, type ReactNode } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';

import { useAuth } from '../auth/auth-context';
import { LoginPage } from '../auth/login-page';
import { ApplicationShell } from '../shell/application-shell';
import type { NavigationItem } from '../shell/application-shell';

const TodayPage = lazy(() =>
  import('../today/today-page').then((module) => ({ default: module.TodayPage })),
);
const InboxPage = lazy(() =>
  import('../inbox/inbox-page').then((module) => ({ default: module.InboxPage })),
);
const DossiersPage = lazy(() =>
  import('../dossiers/dossiers-page').then((module) => ({ default: module.DossiersPage })),
);
const DossierDetailPage = lazy(() =>
  import('../dossiers/dossiers-page').then((module) => ({ default: module.DossierDetailPage })),
);
const ProposalsPage = lazy(() =>
  import('../proposals/proposals-page').then((module) => ({ default: module.ProposalsPage })),
);
const ArbitrationPage = lazy(() =>
  import('../proposals/proposals-page').then((module) => ({ default: module.ArbitrationPage })),
);
const MeetingsPage = lazy(() =>
  import('../meetings/meetings-page').then((module) => ({ default: module.MeetingsPage })),
);
const ActionsPage = lazy(() =>
  import('../actions/actions-page').then((module) => ({ default: module.ActionsPage })),
);
const NotificationsPage = lazy(() =>
  import('../notifications/notifications-page').then((module) => ({
    default: module.NotificationsPage,
  })),
);
const AgendaPage = lazy(() =>
  import('../agenda/agenda-page').then((module) => ({ default: module.AgendaPage })),
);
const MemoryPage = lazy(() =>
  import('../memory/memory-page').then((module) => ({ default: module.MemoryPage })),
);
const AdministrationPage = lazy(() =>
  import('../admin/administration-page').then((module) => ({ default: module.AdministrationPage })),
);
const AccountPage = lazy(() =>
  import('../account/account-page').then((module) => ({ default: module.AccountPage })),
);

export const navigationItems = [
  {
    path: '/aujourdhui',
    label: 'Aujourd’hui',
    shortLabel: 'Aujourd’hui',
    icon: 'today',
    group: 'work',
    mobile: true,
  },
  { path: '/actions', label: 'Actions', icon: 'action', group: 'work', mobile: true },
  {
    path: '/proposer',
    label: 'Proposer un sujet',
    shortLabel: 'Proposer',
    icon: 'proposal',
    group: 'work',
    mobile: true,
  },
  { path: '/arbitrage', label: 'Arbitrage', icon: 'arbitration', group: 'work', ownerOnly: true },
  {
    path: '/notifications',
    label: 'Notifications',
    shortLabel: 'Alertes',
    icon: 'notification',
    group: 'work',
    mobile: true,
  },
  { path: '/inbox', label: 'Inbox Direction', icon: 'inbox', group: 'knowledge' },
  { path: '/dossiers', label: 'Dossiers', icon: 'folder', group: 'knowledge' },
  { path: '/reunions', label: 'Réunions', icon: 'meeting', group: 'knowledge' },
  { path: '/agenda', label: 'Agenda', icon: 'calendar', group: 'knowledge' },
  { path: '/memoire', label: 'Recherche', icon: 'search', group: 'knowledge', mobile: true },
  {
    path: '/administration',
    label: 'Administration',
    icon: 'admin',
    group: 'settings',
    directionOnly: true,
  },
  { path: '/mon-compte', label: 'Mon compte', icon: 'account', group: 'settings' },
] as const satisfies readonly NavigationItem[];

function LoadingRoute() {
  return (
    <p className="state-card state-card--loading" role="status">
      Chargement de l’espace.
    </p>
  );
}

function RouteBoundary({ children }: { children: ReactNode }) {
  return <Suspense fallback={<LoadingRoute />}>{children}</Suspense>;
}

export function ApplicationRoutes() {
  const { loading, member, unavailable } = useAuth();
  if (loading)
    return (
      <main className="auth-page">
        <LoadingRoute />
      </main>
    );
  if (unavailable)
    return (
      <main className="auth-page">
        <section className="state-card">
          <h1>Connexion non configurée</h1>
          <p>
            Le backend Supabase est requis pour ouvrir une session. Aucune donnée locale ne remplace
            l’authentification.
          </p>
        </section>
      </main>
    );
  if (!member) return <LoginPage />;
  return (
    <Routes>
      <Route element={<ApplicationShell navigationItems={navigationItems} />}>
        <Route index element={<Navigate replace to="/aujourdhui" />} />
        <Route
          path="/aujourdhui"
          element={
            <RouteBoundary>
              <TodayPage />
            </RouteBoundary>
          }
        />
        <Route
          path="/inbox"
          element={
            <RouteBoundary>
              <InboxPage />
            </RouteBoundary>
          }
        />
        <Route
          path="/proposer"
          element={
            <RouteBoundary>
              <ProposalsPage />
            </RouteBoundary>
          }
        />
        <Route
          path="/arbitrage"
          element={
            <RouteBoundary>
              <ArbitrationPage />
            </RouteBoundary>
          }
        />
        <Route
          path="/dossiers"
          element={
            <RouteBoundary>
              <DossiersPage />
            </RouteBoundary>
          }
        />
        <Route
          path="/dossiers/:dossierId"
          element={
            <RouteBoundary>
              <DossierDetailPage />
            </RouteBoundary>
          }
        />
        <Route
          path="/reunions"
          element={
            <RouteBoundary>
              <MeetingsPage />
            </RouteBoundary>
          }
        />
        <Route
          path="/actions"
          element={
            <RouteBoundary>
              <ActionsPage />
            </RouteBoundary>
          }
        />
        <Route
          path="/agenda"
          element={
            <RouteBoundary>
              <AgendaPage />
            </RouteBoundary>
          }
        />
        <Route
          path="/memoire"
          element={
            <RouteBoundary>
              <MemoryPage />
            </RouteBoundary>
          }
        />
        <Route
          path="/notifications"
          element={
            <RouteBoundary>
              <NotificationsPage />
            </RouteBoundary>
          }
        />
        <Route
          path="/administration"
          element={
            <RouteBoundary>
              <AdministrationPage />
            </RouteBoundary>
          }
        />
        <Route
          path="/mon-compte"
          element={
            <RouteBoundary>
              <AccountPage />
            </RouteBoundary>
          }
        />
      </Route>
      <Route path="*" element={<Navigate replace to="/aujourdhui" />} />
    </Routes>
  );
}
